// src/utils/champsModifies.ts — SCE Issues
// Ce qu'un événement de l'activité a changé sur la fiche, retrouvé dans son
// texte (02/10). Sert aux pastilles « Modifié il y a 2 h » : cliquer une ligne
// de l'activité récente ouvre la fiche, et les champs touchés sont marqués.
//
// ─── Pourquoi lire le texte ───
//
// L'événement ne porte que du texte (`resumerModification`, une ligne par
// champ). Le format est fixe et écrit par UNE fonction : on le relit ici, ce
// qui marche aussi pour les événements d'avant cette fonctionnalité, sans
// colonne de plus dans SharePoint. Le test refait l'aller-retour avec
// `resumerModification` pour que les deux ne divergent pas.
//
// Seuls les champs que la fiche affiche à un endroit précis sont repris :
// titre, description, métier, criticité, famille, phase, équipement. Les
// références qualité, le N° de plan ou l'EDE n'ont pas de pastille.
//
// Pur : ni React, ni réseau.

import { A_QUALIFIER, type Commentaire } from '@/types/issue.types'

/** Où la fiche pose la pastille. */
export type CibleModification =
  | 'titre'
  | 'description'
  | 'metier'
  | 'criticite'
  | 'famille'
  | 'phase'
  | 'equipement'

/** Cible → l'ancienne valeur, en texte (vide : le champ était vide). */
export type ChampsModifies = ReadonlyMap<CibleModification, string>

/** Libellés écrits par `resumerModification`, tels quels. */
const CIBLE_PAR_LIBELLE: Readonly<Record<string, CibleModification | 'of' | 'pn'>> = {
  'Titre': 'titre',
  'Métier': 'metier',
  'Famille': 'famille',
  'Criticité': 'criticite',
  'Phase': 'phase',
  'N° de série': 'equipement',
  'OF': 'of',
  'PN': 'pn',
}

/** Une ligne « Libellé : avant → après » (l'après peut porter un « — suivi par … »). */
const LIGNE_CHAMP = /^(Titre|Métier|Famille|Criticité|Phase|N° de série|OF|PN) : (.*)$/
const LIGNE_DESCRIPTION = /^Description (ajoutée|modifiée|retirée)\./
const DEBUT_COMMENTAIRE_FIN = /^Commentaire de fin /

export function champsModifies(e: Pick<Commentaire, 'type' | 'contenu'>): ChampsModifies {
  if (e.type === 'Qualification') return lireQualification(e.contenu)
  if (e.type === 'Modification') return lireModification(e.contenu)
  return new Map()
}

/** « famille « X » · criticité « Y » » : un problème ne se qualifie qu'une fois, depuis « À qualifier ». */
function lireQualification(contenu: string): ChampsModifies {
  const champs = new Map<CibleModification, string>()
  if (/famille « /.test(contenu)) champs.set('famille', A_QUALIFIER)
  if (/criticité « /.test(contenu)) champs.set('criticite', A_QUALIFIER)
  return champs
}

function lireModification(contenu: string): ChampsModifies {
  const champs = new Map<CibleModification, string>()
  // Équipement : le N° de série prime, puis le projet, puis l'OF — une seule
  // pastille, une seule ancienne valeur.
  let projet: string | undefined
  let of: string | undefined
  const lignes = contenu.split('\n')

  for (let i = 0; i < lignes.length; i++) {
    const ligne = lignes[i] ?? ''

    const description = LIGNE_DESCRIPTION.exec(ligne)
    if (description) {
      // « Ancienne version :\n« … » » — le texte cité peut contenir des
      // retours à la ligne : on le lit jusqu'au « Commentaire de fin », le
      // seul champ écrit après lui, ou jusqu'à la fin.
      let fin = lignes.length
      for (let j = i + 1; j < lignes.length; j++) {
        if (DEBUT_COMMENTAIRE_FIN.test(lignes[j] ?? '')) {
          fin = j
          break
        }
      }
      const cite = lignes.slice(i + 1, fin).join('\n')
      champs.set('description', description[1] === 'ajoutée' ? '' : citation(cite.replace(/^Ancienne version :\n?/, '')))
      i = fin - 1
      continue
    }

    const m = LIGNE_CHAMP.exec(ligne)
    if (!m) continue
    const cible = CIBLE_PAR_LIBELLE[m[1] ?? '']
    if (!cible) continue
    const avant = avantDe(cible, m[2] ?? '')
    if (cible === 'of') of = avant
    else if (cible === 'pn') projet = avant
    else champs.set(cible, avant)
  }

  if (!champs.has('equipement')) {
    if (projet !== undefined) champs.set('equipement', `projet ${projet}`)
    else if (of !== undefined) champs.set('equipement', `OF ${of}`)
  }
  return champs
}

/** L'ancienne valeur d'une ligne « avant → après » (« — » : le champ était vide). */
function avantDe(cible: CibleModification | 'of' | 'pn', reste: string): string {
  if (cible === 'titre') {
    // « ancien » → « nouveau »
    const m = /^« ([\s\S]*) » → « [\s\S]* »$/.exec(reste)
    return m?.[1] ?? reste
  }
  const avant = reste.split(' → ')[0] ?? ''
  return avant === '—' ? '' : avant.trim()
}

/** Retire les guillemets français qui entourent un texte cité. */
function citation(texte: string): string {
  return texte.trim().replace(/^« ?/, '').replace(/ ?»$/, '').trim()
}
